import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';
import { EMPTY } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { VoucherService } from './services/voucher.service';
import { OfferCoupon } from '../../../../../services/fidelity/fidelity';
import { decodeVoucherToken } from '../helpers/voucher-url.helper';
import JsBarcode from 'jsbarcode';
import { CastofidMiniStatisticService } from '../../../../../services/fidelity/castofid-mini-statistic.service';
import { StatisticTypeEnum } from '../../../../../enum/statistic-type.enum';

/**
 * Page publique d'affichage d'un bon de réduction fidélité.
 * Accessible via l'URL `/voucher/:token` (sans authentification).
 *
 * Le token est un Base64 encodant `{ code, kfId }` — ni le code du bon
 * ni l'identifiant client ne sont lisibles en clair dans la barre d'adresse.
 *
 * Le vendeur affiche le QR code du bon sur son terminal ; le client le scanne
 * avec son téléphone et arrive sur cette page qui récupère les informations
 * du bon via l'API et affiche un QR code à faire lire en caisse.
 */
@Component({
  selector: 'ng-casto-fidelity-voucher-public-access',
  templateUrl: './fidelity-voucher-public-access.component.html',
  styleUrl: './fidelity-voucher-public-access.component.scss',
})
export class FidelityVoucherPublicAccessComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly voucherService = inject(VoucherService);
  private readonly castofidMiniStatisticService = inject(CastofidMiniStatisticService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly datePipe = new DatePipe('fr-FR');

  private barcodeEl?: ElementRef<SVGElement>;

  @ViewChild('barcode')
  private set barcodeElement(barcodeEl: ElementRef<SVGElement> | undefined) {
    this.barcodeEl = barcodeEl;
    this.renderBarcode();
  }

  protected voucher: OfferCoupon | null = null;
  protected loading = true;
  protected error = false;

  get code(): string {
    return this.voucher?.code ?? '';
  }

  get isExpired(): boolean {
    return !this.voucher?.usable || !!this.voucher?.cancelled;
  }

  get formattedEndDate(): string {
    return this.datePipe.transform(this.voucher?.usabilityEndDate, 'dd/MM/yyyy') ?? '';
  }

  ngOnInit(): void {
    const rawToken = this.route.snapshot.paramMap.get('token') ?? '';
    const payload = decodeVoucherToken(rawToken);

    if (!payload) {
      this.error = true;
      this.loading = false;
      return;
    }

    this.voucherService.getVoucherInfo(payload.code, payload.kfId).pipe(
      tap(voucher => {
        this.voucher = voucher;
        void this.castofidMiniStatisticService.send(StatisticTypeEnum.CASTOFID_MINI_COUPON_DOWNLOADED_COUNT);
      }),
      catchError(() => {
        this.error = true;
        return EMPTY;
      }),
      finalize(() => {
        this.loading = false;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private renderBarcode(): void {
    if (!this.barcodeEl?.nativeElement || !this.voucher?.code) return;
    JsBarcode(this.barcodeEl.nativeElement, this.voucher.code, {
      format: 'CODE128',
      displayValue: false,
      margin: 0,
      width: 1.5,
      height: 60,
    });
  }
}
